import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { 
  Phone, 
  Mail, 
  MessageCircle, 
  Instagram, 
  Youtube, 
  Clock,
  MapPin,
  Globe,
  Building,
  Users,
  Shield,
  Zap,
  Sparkles,
  ArrowRight
} from 'lucide-react';

function Contact() {
  const contactData = useSelector(state => state.Data.contactData);
  const contentData = useSelector(state => state.Data.contentData);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Contact methods
  const contactMethods = [
    {
      icon: <Phone color='blue' className="w-6 h-6" />,
      title: "Telefon",
      values: [contactData?.phone_1, contactData?.phone_2].filter(Boolean),
      links: [contactData?.phone_1, contactData?.phone_2].filter(Boolean).map(phone => `tel:${phone}`),
      color: "from-blue-500 to-blue-600",
      bg: "bg-blue-50",
      action: "Zəng edin"
    },
    {
      icon: <Mail color='blue' className="w-6 h-6" />,
      title: "Email",
      values: [contactData?.email],
      links: [`mailto:${contactData?.email}`],
      color: "from-amber-500 to-amber-600",
      bg: "bg-amber-50",
      action: "Email göndərin"
    },
    {
      icon: <MessageCircle color='blue' className="w-6 h-6" />,
      title: "WhatsApp",
      values: [contactData?.whatsapp_1],
      links: [`https://wa.me/${contactData?.whatsapp_1?.replaceAll(" ", "")?.replace("+", "")}`],
      color: "from-emerald-500 to-emerald-600",
      bg: "bg-emerald-50",
      action: "Mesaj yazın"
    },
    {
      icon: <Globe color='blue' className="w-6 h-6" />,
      title: "Sosial Media",
      values: ["Instagram", "YouTube", "Facebook"],
      links: [contactData?.instagram, contactData?.youtube, contactData?.facebook],
      color: "from-purple-500 to-purple-600",
      bg: "bg-purple-50",
      action: "İzləyin"
    }
  ];

 
  return (
    <main className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Hero Section */}
      <div className="relative overflow-hidden bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900 py-20">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-1/4 left-1/4 w-64 h-64 border-2 border-blue-400 rounded-full animate-pulse"></div>
          <div className="absolute bottom-1/4 right-1/4 w-48 h-48 border-2 border-amber-400 rounded-full animate-pulse"></div>
        </div>

        <div className="relative container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-3 px-4 py-2 bg-gradient-to-r from-blue-500/20 to-amber-500/20 rounded-full mb-6 border border-blue-400/30 backdrop-blur-sm">
              <Sparkles className="w-4 h-4 text-blue-300" />
              <span className="text-sm font-medium text-blue-100">{contactData?.contact_us_sub}</span>
              <Sparkles className="w-4 h-4 text-amber-300" />
            </div>
            
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 tracking-tight">
              <span className="bg-gradient-to-r from-white via-blue-100 to-white bg-clip-text text-transparent">
                {contentData?.contact_title || "Əlaqə"}
              </span>
            </h1>
            
            <p className="text-xl text-blue-100 max-w-2xl mx-auto leading-relaxed">
             {contactData?.contact_us_desc}
            </p>
          </div>
        </div>
      </div>

      {/* Contact Information Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            {/* Contact Cards Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
              {contactMethods.map((method, index) => (
                <div key={index} className="group">
                  <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm hover:shadow-xl transition-all duration-500 hover:-translate-y-1 h-full">
                    {/* Icon */}
                    <div className={`w-14 h-14 ${method.bg} rounded-xl flex items-center justify-center mb-4 shadow-md group-hover:shadow-lg transition-shadow`}>
                      <div className={`bg-gradient-to-r ${method.color} bg-clip-text text-transparent`}>
                        {method.icon}
                      </div>
                    </div>
                    
                    {/* Title */}
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{method.title}</h3>
                    
                    {/* Values */}
                    <div className="space-y-2 mb-4">
                      {method.values.map((value, idx) => (
                        <div key={idx}>
                          {method.links[idx] ? (
                            <a
                              href={method.links[idx]}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-gray-700 hover:text-blue-600 font-medium transition-colors block"
                            >
                              {value}
                            </a>
                          ) : (
                            <span className="text-gray-700 font-medium">{value}</span>
                          )}
                        </div>
                      ))}
                    </div>
                    
                    {/* Action */}
                    {method.links[0] && (
                      <a
                        href={method.links[0]}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-sm font-medium text-blue-600 hover:text-blue-700 group-hover:gap-3 transition-all"
                      >
                        <span>{method.action}</span>
                        <ArrowRight className="w-4 h-4" />
                      </a>
                    )}
                    
                    {/* Accent Line */}
                    <div className={`h-1 w-12 bg-gradient-to-r ${method.color} mt-4 rounded-full transition-all duration-500 group-hover:w-16`}></div>
                  </div>
                </div>
              ))}
            </div>

          
            {/* Working Hours & Emergency */}
            <div className="grid lg:grid-cols-2 gap-8">
              {/* Working Hours */}
              <div className="bg-gradient-to-r from-blue-50 to-amber-50 rounded-2xl p-8 border border-blue-100">
                <div className="flex items-center gap-4 mb-8">
                  <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-md">
                    <Clock className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">İş Saatları</h3>
                    <p className="text-gray-600">Ofis və texniki dəstək vaxtları</p>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div className="flex justify-between items-center py-3 border-b border-blue-100">
                    <span className="text-gray-700 font-medium">Bazar ertəsi - Cümə</span>
                    <span className="text-gray-900 font-bold">{contentData?.open_time || "09:00 - 18:00"}</span>
                  </div>
                  <div className="flex justify-between items-center py-3 border-b border-blue-100">
                    <span className="text-gray-700 font-medium">Şənbə</span>
                    <span className="text-gray-900 font-bold">{contentData?.close_time || "10:00 - 16:00"}</span>
                  </div>
                  <div className="flex justify-between items-center py-3">
                    <span className="text-gray-700 font-medium">Bazar</span>
                    <span className="text-gray-900 font-bold">İstirahət</span>
                  </div>
                </div>
              </div>

              {/* Emergency Contact */}
              <div className="bg-gradient-to-r from-red-50 to-amber-50 rounded-2xl p-8 border border-red-100 relative overflow-hidden">
                <div className="absolute -top-20 -right-20 w-40 h-40 bg-gradient-to-br from-red-400/20 to-amber-400/20 rounded-full blur-2xl"></div>
                
                <div className="relative z-10">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-14 h-14 bg-gradient-to-br from-red-500 to-red-600 rounded-xl flex items-center justify-center shadow-md">
                      <Zap className="w-7 h-7 text-white" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900">24/7 Emergency</h3>
                      <p className="text-gray-600">Təcili və qəza vəziyyətləri</p>
                    </div>
                  </div>
                  
                  <div className="mb-6">
                    <div className="text-3xl font-bold text-gray-900 mb-2">{contactData?.phone_1}</div>
                    <p className="text-gray-700">
                      Elektrik qəzası, təcili təmir və ya qəza vəziyyətində dərhal əlaqə saxlayın
                    </p>
                  </div>
                  
                  <div className="flex flex-wrap gap-4">
                    <a
                      href={`tel:${contactData?.phone_1}`}
                      className="inline-flex items-center gap-3 px-6 py-3 bg-gradient-to-r from-red-500 to-red-600 text-white font-bold rounded-xl hover:from-red-600 hover:to-red-700 shadow-lg hover:shadow-xl transition-all duration-300"
                    >
                      <Phone className="w-5 h-5" />
                      <span>Dərhal Zəng Edin</span>
                    </a>
                    
                    <a
                      href={`https://wa.me/${contactData?.whatsapp_1?.replaceAll(" ", "")?.replace("+", "")}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-3 px-6 py-3 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white font-bold rounded-xl hover:from-emerald-600 hover:to-emerald-700 shadow-lg hover:shadow-xl transition-all duration-300"
                    >
                      <MessageCircle className="w-5 h-5" />
                      <span>WhatsApp Mesajı</span>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}

export default Contact;